import { Trans, useLingui } from '@lingui/react/macro';
import { Typography } from '@remarkable/ark-web';
import type { ReactNode } from '@tanstack/react-router';

import { ComponentLocations } from 'src/analytics/trackingTypes';
import { Button, Modal, NotificationBox } from 'src/components';

export const AbortCancelSubscriptionModal = ({
  trigger,
  isAborting,
  abortError,
  onAbort,
}: {
  trigger: ReactNode;
  isAborting: boolean;
  abortError: Error | null;
  onAbort: () => void;
}) => {
  const { t } = useLingui();

  return (
    <Modal.Root>
      <Modal.Trigger asChild>{trigger}</Modal.Trigger>

      <Modal.Content title={t`Undo cancellation?`}>
        <Typography variant="body-md" className="mb-16">
          <Trans>
            Undo your cancelation if you would like to keep subscription
            features for you and your team.
          </Trans>
        </Typography>

        {abortError && (
          <NotificationBox
            error={abortError}
            title={t`Something went wrong`}
            className="mt-16"
          >
            <Trans>
              Please try again or contact support if the problem persists.
            </Trans>
          </NotificationBox>
        )}

        <div className="mt-32 flex flex-col justify-center gap-16 ls:flex-row">
          <Modal.Close asChild>
            <Button
              variant="secondary"
              size="medium"
              className="w-full xs:w-fit"
              analytics={{
                text: 'Close',
                location: ComponentLocations.MANAGE_CONNECT.SUBSCRIPTION_PAGE,
                action: 'close undo cancelation modal',
              }}
            >
              <Trans>Close</Trans>
            </Button>
          </Modal.Close>

          <Button
            size="medium"
            className="w-full xs:w-fit"
            loading={isAborting}
            onClick={() => {
              onAbort();
            }}
            analytics={{
              text: 'Undo cancelation',
              location: ComponentLocations.MANAGE_CONNECT.SUBSCRIPTION_PAGE,
              action: 'undo cancelation',
            }}
          >
            <Trans>Undo cancelation</Trans>
          </Button>
        </div>
      </Modal.Content>
    </Modal.Root>
  );
};
